import { useCellReviews } from "@platform/btx-plan-designer";
import { renderIconToElement } from "@platform/design-system";
import { useDialogs } from "@platform/hooks";
import { useCallback } from "react";

import type {
  CommentDialogData,
  DialogName,
} from "@platform/btx-plan-designer";
import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
} from "ag-grid-community";

export interface CellReviewCellIdentity {
  planId: string;
  fieldPath: string;
}

export type ContextMenuItemsGetter = (
  params: GetContextMenuItemsParams,
) => (DefaultMenuItem | MenuItemDef)[];

/**
 * Builds the shared "Highlight" / "Needs Review" AG Grid context menu items.
 * Callers supply `deriveCell` to resolve the {planId, fieldPath} identity for
 * a grid cell from their own row/column shape (proposal plan + benefit key
 * for Comps, carrier + coverage for Responses). Must be called inside both
 * CellReviewProvider and DialogProvider.
 */
export const useCellReviewContextMenu = (
  deriveCell: (
    params: GetContextMenuItemsParams,
  ) => CellReviewCellIdentity | null,
): ContextMenuItemsGetter => {
  const cellReviews = useCellReviews();
  const { openDialog } = useDialogs<DialogName>();

  return useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const cell = deriveCell(params);
      if (!cell) return [];

      const { planId, fieldPath } = cell;
      const isHighlighted = cellReviews.getBrokerHighlight(planId, fieldPath);
      const existingReview = cellReviews.getReview(planId, fieldPath);

      const items: MenuItemDef[] = [
        {
          name: `${isHighlighted ? "Remove Highlight" : "Broker Highlight"}`,
          icon: renderIconToElement("highlight", "sm", "muted"),
          action: () => {
            if (isHighlighted) {
              cellReviews.clearBrokerHighlight(planId, fieldPath);
            } else {
              cellReviews.setBrokerHighlight(planId, fieldPath);
            }
          },
        },
        existingReview
          ? {
              name: "Edit/View Review",
              icon: renderIconToElement("comment", "sm", "muted"),
              action: () => {
                const dialogData: CommentDialogData = {
                  title: "Edit/View Review",
                  currentComment: existingReview.comment ?? "",
                  planId,
                  fieldPath,
                  createdAt: existingReview.createdAt,
                  updatedAt: existingReview.updatedAt,
                  onSubmit: (comment: string) => {
                    cellReviews.upsertReview(
                      planId,
                      fieldPath,
                      comment || null,
                    );
                  },
                };
                openDialog<CommentDialogData>("comment", dialogData);
              },
            }
          : {
              name: "Needs Review",
              icon: renderIconToElement("comment", "sm", "muted"),
              action: () => {
                const dialogData: CommentDialogData = {
                  title: "Needs Review",
                  currentComment: "",
                  planId,
                  fieldPath,
                  onSubmit: (comment: string) => {
                    cellReviews.upsertReview(
                      planId,
                      fieldPath,
                      comment || null,
                    );
                  },
                };
                openDialog<CommentDialogData>("comment", dialogData);
              },
            },
      ];

      if (existingReview) {
        items.push({
          name: "Clear Review",
          icon: renderIconToElement("remove", "sm", "muted"),
          action: () => cellReviews.clearReview(planId, fieldPath),
        });
      }

      return items;
    },
    [cellReviews, deriveCell, openDialog],
  );
};
